import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";

const LayoutContext = createContext(null);

// LocalStorage keys
const LS_SIDEBAR = "sb-collapsed";

export function LayoutProvider({ children }) {
  // ✅ collapsed: carga inicial desde localStorage (sin parpadeo)
  const [collapsed, setCollapsed] = useState(() => {
    // ✅ compatible con Next.js / SSR
    if (typeof window === "undefined") return false;

    try {
      const saved = localStorage.getItem(LS_SIDEBAR);
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState([]);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // actions
  const toggleSidebar = () => setCollapsed((p) => !p);
  const toggleMobileSidebar = () => setMobileOpen((p) => !p);

  const toggleNavItem = (id) => {
    if (collapsed) return;
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleProfileMenu = () => setProfileOpen((p) => !p);

  // ✅ cerrar profile al click afuera
  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // ✅ guardar collapsed en localStorage cada vez que cambie
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      localStorage.setItem(LS_SIDEBAR, JSON.stringify(collapsed));
    } catch (err) {
      console.warn("No se pudo guardar el estado del sidebar", err);
    }
  }, [collapsed]);

  const value = useMemo(
    () => ({
      // state
      collapsed,
      mobileOpen,
      expandedItems,
      profileOpen,
      profileRef,
      // actions
      toggleSidebar,
      toggleMobileSidebar,
      toggleNavItem,
      toggleProfileMenu,
      // setters (si los usas desde afuera)
      setCollapsed,
      setMobileOpen,
      setExpandedItems,
      setProfileOpen,
    }),
    [collapsed, mobileOpen, expandedItems, profileOpen]
  );

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
}

export function useSbLayout() {
  const ctx = useContext(LayoutContext);
  if (!ctx) {
    throw new Error("useSbLayout debe usarse dentro de <LayoutProvider />");
  }
  return ctx;
}
